import "./App.css";
import "./montserrat.css";
import "./css/transitions.css";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import React, { useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

import Home from "./website/homeEditry";
import { preloadCriticalAssets } from "./utils/preload";
import { debugPreloadState } from "./utils/debug";
import { initializeNavigationTracking, applyInstantBikeCategoryImages } from "./utils/imageTransitionUtils";
import Error from "./website/error";

import Nukeproof from "./website/o-nukeproof";
import Prodejci from "./website/prodejci";
import Novinky from "./website/novinky";

import SeznamKol from "./website/seznamkol";
import BikeExample from "./website/bike-example";
import ArticleExample from "./website/article-example";
import Configurator from "./website/configurator";
import ModalBox from "./components/modal";
import ModalTable from "./components/tableModal";

import Footer from "./components/footer";
import Loader from "./components/loader";
import Topbar from "./components/topbar";
import ScrollToTop from "./components/scrollup";
import Kontakt from "./website/kontakt";
import Login from './website/login';
import { EditryAdminOverlay, useEditry } from '@lazylabs-dev/editry-react';

import { HelmetProvider } from "react-helmet-async";
import { GlobalSeo } from "./components/GlobalSeo";

import Pruvodce from "./website/pruvodce";
import Zaruka from "./website/zaruka";

function App() {
  const location = useLocation();
  const normalizedPathname = location.pathname.replace(/\/+$/, '') || '/';
  const isLoginRoute = normalizedPathname === '/login';
  const { overlayEnabled, adminSession } = useEditry();
  const isLoggedIn = Boolean(adminSession?.token);
  const isEditModeActive = overlayEnabled && Boolean(adminSession?.token);
  const [loader, setLoader] = useState(true);
  const [mode, setMode] = useState("dark");
  const [preloadComplete, setPreloadComplete] = useState(false);
  const [isHidingLoader, setIsHidingLoader] = useState(false);

  const handleEditableClickCapture = (event) => {
    if (!isEditModeActive) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const isEditableElement = target.closest('[data-editry-key], [data-editry-news-id]');
    if (!isEditableElement) {
      return;
    }

    const clickableWrapper = target.closest('a[href], button');
    if (clickableWrapper) {
      event.preventDefault();
    }
  };
  
  // Initialize navigation tracking on mount
  useEffect(() => {
    initializeNavigationTracking();
  }, []);

  // Force complete the loader after a maximum time to prevent infinite loading
  useEffect(() => {
    // Create a persistent flag to track navigation history
    if (typeof sessionStorage !== 'undefined') {
      const hasVisitedHome = sessionStorage.getItem('hasVisitedHome');
      const isReturningToHome = location.pathname === "/" && hasVisitedHome === 'true';
      
      // Always mark that we've visited home
      sessionStorage.setItem('hasVisitedHome', 'true');
      
      // If returning to home, ensure all images show instantly
      if (isReturningToHome) {
        //console.log('Returning to home page, applying cached images immediately');
        
        // Apply instant transitions to bike category images
        applyInstantBikeCategoryImages();
        
        // Import our enhanced image cache utilities and apply them
        import('./utils/globalImageCache').then(cacheModule => {
          // Apply cached images to all bike elements
          const elements = document.querySelectorAll('.option2');
          elements.forEach(el => {
            const url = el.dataset.imageSrc;
            if (url) {
              cacheModule.setBackgroundFromCache(el.id, url);
            }
          });
          
          // Also start preloading all bike images in the background for future use
          setTimeout(() => {
            cacheModule.preloadAllBikeCategoryImages();
          }, 100);
        });
      }
    }

    const forceCompleteTimeout = setTimeout(() => {
      if (loader) {
        console.warn("Force completing loader after timeout");
        if (typeof debugPreloadState === 'function') {
          debugPreloadState(); // Log debug info before force completion
        }
        setPreloadComplete(true);
      }
    }, 2000); // Force complete after 2 seconds max
    
    return () => clearTimeout(forceCompleteTimeout);
  }, [loader, location.pathname]);

  // Check if preloading is needed or already complete
  useEffect(() => {
    // Only additional preload for home page
    if (location.pathname === "/" || location.pathname === "") {
      // For the home route we only preload critical assets here.
      try {
        preloadCriticalAssets()
          .then(() => {
            setPreloadComplete(true);
          })
          .catch(error => {
            console.error("Error preloading assets:", error);
            setPreloadComplete(true);
          });
      } catch (error) {
        console.error("Error in preload process:", error);
        setPreloadComplete(true);
      }
    } else {
      // For non-home pages, no delay
      setPreloadComplete(true);
    }
  }, [location.pathname]);

  // Hide loader when preloading is complete
  useEffect(() => {
    if (preloadComplete) {
      //console.log("Preload complete, starting fade-out transition");
      // Start the fade transition
      setIsHidingLoader(true);
      
      // Only remove the loader component from DOM after the transition completes
      setTimeout(() => {
        //console.log("Removing loader from DOM after transition");
        setLoader(false);
      }, 600); // Match duration with CSS transition
    }
  }, [preloadComplete]);

  useEffect(() => {
    if (mode === "light") {
      document.body.style.backgroundColor = 'rgb(255, 255, 255)';
    } else {
      document.body.style.backgroundColor = 'rgb(255, 255, 255)';
    }
  }, [mode]);

  const [modal, setModal] = useState(false)
  const [tableModal, setTableModal] = useState({ show: false, id: null })

  const [titleModal, setTitleModal] = useState("null")
  const [textModal, setTextModal] = useState("null")
  const [imageModal, setImageModal] = useState("null")


  return (
    <div className="light">
      <HelmetProvider>
        <GlobalSeo />
      <ToastContainer theme="light" position="top-right" autoClose={5000} hideProgressBar={false} closeOnClick draggable pauseOnHover />
      <div className="background overlay-transition-container">
        {/* Both content and loader rendered simultaneously */}
        <div
          className={`site-content ${isEditModeActive ? 'edit-mode-active' : ''}`}
          onClickCapture={handleEditableClickCapture}
          style={{ 
          opacity: loader ? 0 : 1,
          transition: "opacity 0.3s ease-in",
          transitionDelay: "0.3s",
          position: "relative",
          zIndex: 1
        }}>
          <div className="topbar-container">
            <Topbar mode="light"/>
          </div>
          
          <div className="content-container">
            <ScrollToTop/>

            {modal && 
            <ModalBox imageModal={imageModal} setModal={setModal} titleModal={titleModal} textModal={textModal}/>
            }
            {tableModal.show && 
            <ModalTable setModal={setTableModal} tableId={tableModal.id} />
            }

            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/o-nukeproof" element={<Nukeproof />} />
              <Route path="/prodejci" element={<Prodejci />} />
              <Route path="/kontakt" element={<Kontakt />} />
              <Route path="/kola/:kategorie" element={<SeznamKol bikeData={[]} />} />
              <Route path="/articles" element={<Novinky />} />
              <Route path="/konfigurator" element={<Configurator />} />

              <Route path="/pruvodce/:category" element={<Pruvodce />} />
              <Route path="/kola/:kategorie/:id" element={<BikeExample bikeData={[]} theme={mode} setTableModal={setTableModal} setModal={setModal} setTitleModal={setTitleModal} setTextModal={setTextModal} setImageModal={setImageModal}/>} />
              <Route path="/article/:id" element={<ArticleExample />} />

              <Route path="/prodlouzena-zaruka" element={<Zaruka />} />

              <Route path="*" element={<Error />} />
            </Routes>
          </div>
          
          <div className="content-container z-index1">
            <Footer setMode={setMode} mode="light"/>
          </div>
        </div>

        {/* Loader on top */}
        {loader && (
          <Loader isHiding={isHidingLoader} />
        )}

        {(!isLoginRoute || isLoggedIn) && (
          <EditryAdminOverlay
            className="editry-overlay-root"
            variant="floating"
            title="Editry Admin"
            showSecretTokenField={false}
            loginPathname="/login"
            currentPathname={normalizedPathname}
          />
        )}
      </div>
      </HelmetProvider>
    </div>
  );
}

export default App;
